/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { store } from './store';
import { RequestOptions } from './httpClient.types';
import {
  refreshTokenAsyncThunk,
  logoutUserAsyncThunk,
} from './users/users.action';
import { globalNavigate } from 'hooks/useNavigationRef';
import { APP_ROUTES } from 'constants/routes';

// Environment variables must be prefixed with REACT_APP_ to be accessible
export const BASE_API_V1 = process.env.REACT_APP_API_URL;

// Create axios instance
const axiosInstance = axios.create({
  baseURL: BASE_API_V1,
  withCredentials: true, // Enable cookies for refresh token
  validateStatus: (status) => {
    // Treat status codes 200-299 as success (don't reject)
    return status >= 200 && status < 300;
  },
});

// Track failed requests for retry
const failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: InternalAxiosRequestConfig;
}> = [];
let isRefreshing = false;

// Request interceptor to inject token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth?.accessToken;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const formType =
      config.data instanceof FormData
        ? 'multipart/form-data'
        : 'application/json';

    if (config.headers) {
      config.headers.Accept = 'application/json';
      config.headers['Content-Type'] = formType;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 and retry with refresh token
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Success responses (200, 201, etc.) pass through unchanged
    return response;
  },
  async (error: AxiosError) => {
    // Only process actual errors (not successful responses)

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _isSilentLogin?: boolean;
    };

    const isLoginRequest = originalRequest.url?.includes('/users/login');
    const isGoogleAuthRequest =
      originalRequest.url?.includes('/users/googleAuth');
    const isLogoutRequest = originalRequest.url?.includes('/users/logout');
    const isRefreshRequest = originalRequest.url?.includes('/session/refresh');
    const isSilentLoginRequest =
      isRefreshRequest && originalRequest._isSilentLogin;
    const isVerifyEmailRequest = originalRequest.url?.includes('/users/verify');

    if (
      !error.response ||
      isLoginRequest ||
      isGoogleAuthRequest ||
      isLogoutRequest ||
      isSilentLoginRequest ||
      isVerifyEmailRequest
    ) {
      // Network error or no response - pass through
      return Promise.reject(error);
    }

    const redirectHomeWithLogout = (omitRedirect = false) => {
      store.dispatch(
        logoutUserAsyncThunk({
          onSuccess: () => {
            // Navigate to home page
            if (!omitRedirect) globalNavigate(APP_ROUTES.HOME);
          },
        })
      );
    };

    const rejectAll = (error: AxiosError) => {
      failedQueue.forEach((prom) => {
        prom.reject(error);
      });
      failedQueue.length = 0;
      return Promise.reject(error);
    };

    const resolveAll = () => {
      failedQueue.forEach((prom) => {
        prom.resolve();
      });
      failedQueue.length = 0;
    };

    // Only handle 401 Unauthorized errors
    if (error.response.status === 401 && !originalRequest._retry) {
      // Skip refresh for login/refresh endpoints to avoid infinite loops
      if (isLoginRequest || isRefreshRequest || isLogoutRequest) {
        if (!isLogoutRequest) redirectHomeWithLogout(true);

        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token (refresh token is in cookie)
        const refreshResult = await store.dispatch(refreshTokenAsyncThunk());
        if (refreshTokenAsyncThunk.fulfilled.match(refreshResult)) {
          // Refresh succeeded, retry original request
          const newToken = store.getState().auth?.accessToken;
          if (newToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          // Process queued requests
          resolveAll();

          return axiosInstance(originalRequest);
        } else {
          // Refresh failed, logout user
          redirectHomeWithLogout();

          // Process queued requests
          return rejectAll(error);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        redirectHomeWithLogout();

        // Process queued requests
        return rejectAll(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For all non-401 errors, pass through without modification
    return Promise.reject(error);
  }
);

const createHttpClient = (
  abortSignal?: AbortSignal,
  isSilentLogin?: boolean
) => {
  // Generic request method
  const request = async <T, B = any>({
    method,
    endpoint,
    payload,
    query,
    headers = {},
    responseType,
  }: RequestOptions<B>): Promise<T> => {
    const config: AxiosRequestConfig & { _isSilentLogin?: boolean } = {
      method,
      url: endpoint,
      data: payload || undefined,
      params: query,
      headers,
      responseType,
      ...(abortSignal && { signal: abortSignal }),
      ...(isSilentLogin !== undefined && { _isSilentLogin: isSilentLogin }),
    };

    const response: AxiosResponse<T> = await axiosInstance(config);
    return response.data;
  };

  // Public API
  return {
    get: <T>(url: string, options: Partial<RequestOptions> = {}) =>
      request<T>({ method: 'GET', endpoint: url, ...options }),

    post: <T, B = any>(
      url: string,
      data: B,
      options: Partial<RequestOptions> = {}
    ) =>
      request<T, B>({
        method: 'POST',
        endpoint: url,
        payload: data,
        ...options,
      }),

    put: <T, B = any>(
      url: string,
      data: B,
      options: Partial<RequestOptions> = {}
    ) =>
      request<T, B>({
        method: 'PUT',
        endpoint: url,
        payload: data,
        ...options,
      }),

    patch: <T>(url: string, options: Partial<RequestOptions> = {}) =>
      request<T>({ method: 'PATCH', endpoint: url, ...options }),

    delete: <T>(url: string, options: Partial<RequestOptions> = {}) =>
      request<T>({ method: 'DELETE', endpoint: url, ...options }),
  };
};

export default createHttpClient;
